/**
 * RevisionCard Component
 *
 * Displays a revision sample or user revision card.
 * Used in Revision Hub to show available samples and revision history.
 */

import { motion } from 'framer-motion'
import { Target, Star, Sparkles, Edit3, CheckCircle, RefreshCw } from 'lucide-react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { Card, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import type { RevisionSample, StoryRevision, ChallengeDifficulty } from '@/types/database'

// =============================================================================
// Revision Sample Card
// =============================================================================

interface SampleCardProps {
  sample: RevisionSample
  /** Whether the child has an in-progress revision for this sample */
  hasInProgress?: boolean
  className?: string
}

const DIFFICULTY_CONFIG: Record<
  ChallengeDifficulty,
  { label: string; color: string; bgColor: string; icon: typeof Star }
> = {
  beginner: {
    label: 'Beginner',
    color: 'text-[var(--color-kids-success)]',
    bgColor: 'bg-[var(--color-kids-success)]/10',
    icon: Star,
  },
  intermediate: {
    label: 'Intermediate',
    color: 'text-[var(--color-kids-secondary)]',
    bgColor: 'bg-[var(--color-kids-secondary)]/10',
    icon: Sparkles,
  },
  advanced: {
    label: 'Advanced',
    color: 'text-[var(--color-kids-accent)]',
    bgColor: 'bg-[var(--color-kids-accent)]/10',
    icon: Target,
  },
}

const FOCUS_AREA_LABELS: Record<string, string> = {
  problem: 'problem',
  goal: 'goal',
  obstacle: 'obstacle',
  turning_point: 'turning point',
  resolution: 'resolution',
  change: 'character change',
}

export function SampleCard({
  sample,
  hasInProgress = false,
  className,
}: SampleCardProps) {
  const difficultyConfig = DIFFICULTY_CONFIG[sample.difficulty]
  const DifficultyIcon = difficultyConfig.icon

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className={className}
    >
      <Card hoverable variant="default" className="relative overflow-hidden">
        <CardContent className="p-5">
          {/* Header */}
          <div className="flex items-start gap-3 mb-3">
            <span className="text-3xl">
              <Edit3 className="w-8 h-8 text-[var(--color-kids-primary)]" />
            </span>
            <div className="flex-1 min-w-0">
              <h3 className="font-display font-bold text-lg text-[var(--color-kids-text)] truncate">
                {sample.title}
              </h3>
              <div className="flex items-center gap-2 mt-1">
                <span
                  className={cn(
                    'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
                    difficultyConfig.bgColor,
                    difficultyConfig.color
                  )}
                >
                  <DifficultyIcon className="w-3 h-3" />
                  {difficultyConfig.label}
                </span>
              </div>
            </div>
          </div>

          {/* Description */}
          <p className="text-sm text-[var(--color-kids-text-muted)] line-clamp-2 mb-4">
            {sample.description}
          </p>

          {/* Focus areas */}
          {sample.focus_areas.length > 0 && (
            <div className="mb-4">
              <p className="text-xs text-[var(--color-kids-text-muted)] mb-1">
                Focus on improving:
              </p>
              <div className="flex flex-wrap gap-1">
                {sample.focus_areas.map(area => (
                  <span
                    key={area}
                    className="text-xs bg-[var(--color-kids-accent)]/10 text-[var(--color-kids-accent)] px-2 py-0.5 rounded-full"
                  >
                    {FOCUS_AREA_LABELS[area] || area.replace('_', ' ')}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Action */}
          <Link to={`/revise/sample/${sample.id}`}>
            <Button
              variant={hasInProgress ? 'secondary' : 'primary'}
              size="sm"
              className="w-full"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              {hasInProgress ? 'Continue Revising' : 'Start Revision'}
            </Button>
          </Link>
        </CardContent>
      </Card>
    </motion.div>
  )
}

// =============================================================================
// User Revision Card (for revision history)
// =============================================================================

interface RevisionCardProps {
  revision: StoryRevision
  /** Title for the revision (from sample or challenge) */
  title?: string
  className?: string
}

export function RevisionCard({
  revision,
  title = 'Revision',
  className,
}: RevisionCardProps) {
  const isCompleted = revision.status === 'completed'
  const improvementScore = revision.improvement_score ?? 0

  // Determine improvement display
  const getImprovementBadge = () => {
    if (!isCompleted) return null
    if (improvementScore > 10) {
      return { label: 'Great improvement!', color: 'text-[var(--color-kids-success)]', bg: 'bg-[var(--color-kids-success)]/10' }
    }
    if (improvementScore > 0) {
      return { label: 'Good progress', color: 'text-[var(--color-kids-secondary)]', bg: 'bg-[var(--color-kids-secondary)]/10' }
    }
    return { label: 'Keep practicing', color: 'text-[var(--color-kids-text-muted)]', bg: 'bg-[var(--color-kids-surface-alt)]' }
  }

  const improvementBadge = getImprovementBadge()

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className={className}
    >
      <Card hoverable variant="default">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className={cn(
              'w-10 h-10 rounded-full flex items-center justify-center',
              isCompleted
                ? 'bg-[var(--color-kids-success)]/10'
                : 'bg-[var(--color-kids-primary)]/10'
            )}>
              {isCompleted ? (
                <CheckCircle className="w-5 h-5 text-[var(--color-kids-success)]" />
              ) : (
                <Edit3 className="w-5 h-5 text-[var(--color-kids-primary)]" />
              )}
            </div>

            <div className="flex-1 min-w-0">
              <h4 className="font-display font-semibold text-sm text-[var(--color-kids-text)] truncate">
                {title}
              </h4>
              <div className="flex items-center gap-2 mt-0.5">
                <span className={cn(
                  'text-xs font-medium',
                  isCompleted
                    ? 'text-[var(--color-kids-success)]'
                    : 'text-[var(--color-kids-primary)]'
                )}>
                  {isCompleted ? 'Completed' : 'In Progress'}
                </span>
                {improvementBadge && (
                  <span className={cn(
                    'text-xs px-2 py-0.5 rounded-full',
                    improvementBadge.bg,
                    improvementBadge.color
                  )}>
                    {improvementBadge.label}
                  </span>
                )}
              </div>
            </div>

            <Link to={`/revise/${revision.source_type}/${revision.source_type === 'sample' ? revision.sample_id : revision.submission_id}`}>
              <Button variant="ghost" size="sm">
                {isCompleted ? 'View' : 'Continue'}
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )
}

// =============================================================================
// Compact Sample Card (for dashboard)
// =============================================================================

interface CompactSampleCardProps {
  sample: RevisionSample
  className?: string
}

export function CompactSampleCard({
  sample,
  className,
}: CompactSampleCardProps) {
  const difficultyConfig = DIFFICULTY_CONFIG[sample.difficulty]

  return (
    <Link to={`/revise/sample/${sample.id}`}>
      <motion.div
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className={cn(
          'flex items-center gap-3 p-3 rounded-[var(--radius-kid)]',
          'bg-[var(--color-kids-surface)] shadow-[var(--shadow-kid)]',
          'hover:shadow-[var(--shadow-kid-hover)] transition-all',
          className
        )}
      >
        <Edit3 className="w-6 h-6 text-[var(--color-kids-primary)]" />
        <div className="flex-1 min-w-0">
          <h4 className="font-display font-semibold text-sm text-[var(--color-kids-text)] truncate">
            {sample.title}
          </h4>
          <div className="flex items-center gap-2">
            <span className={cn('text-xs font-medium', difficultyConfig.color)}>
              {difficultyConfig.label}
            </span>
          </div>
        </div>
        <RefreshCw className="w-5 h-5 text-[var(--color-kids-text-muted)]" />
      </motion.div>
    </Link>
  )
}

export default SampleCard
